import { colors } from '@/theme/foundations/colors';

// Chakra's own base tokens, which colors.js does not redeclare.
const CHAKRA_BASE = { white: '#FFFFFF', black: '#000000' } as const;

type FlatColorToken = {
  [K in keyof typeof colors]: (typeof colors)[K] extends string ? K : never;
}[keyof typeof colors];

export type ColorToken = (FlatColorToken | keyof typeof CHAKRA_BASE) & string;

const PALETTE: Record<string, unknown> = { ...CHAKRA_BASE, ...colors };

/**
 * A theme-aware colour for places Chakra's style props don't reach: inline
 * `style`, `@kula-ai/icons-core` icons (bare `<svg>`), react-select style
 * functions.
 *
 * `colors['secondary-700']` is a hex literal and stays light in dark mode;
 * `colorVar('secondary-700')` reads the variable Chakra declares for that
 * token (`--kula-colors-secondary-700`, re-declared by the dark palette) and
 * falls back to the same hex wherever no ChakraProvider has mounted.
 */
export const colorVar = (token: ColorToken): string =>
  `var(--kula-colors-${token}, ${PALETTE[token] as string})`;

/**
 * The light `primary-50` tint, which dark mode turns into a 10% primary wash
 * (`--kula-dark-primary-wash`, declared only in dark) instead of the solid
 * dark primary-50.
 */
export const primaryWash = `var(--kula-dark-primary-wash, ${colorVar('primary-50')})`;

/**
 * A light-mode white surface that dark mode paints in the page colour
 * (secondary-25 there) rather than the raised card surface `white` becomes:
 * full-page wrappers and what sits directly on them.
 */
export const pageBg = `var(--kula-dark-secondary-25, ${colorVar('white')})`;

/**
 * Text typed into an input: secondary-700 in light mode, near-white
 * (secondary-900) in dark (design), where secondary-700 reads too dim.
 */
export const inputText = `var(--kula-dark-secondary-900, ${PALETTE['secondary-700'] as string})`;

/**
 * A tooltip drawn as a white card in light mode. The tooltip theme points
 * `white` at its light text colour in dark mode, so `bg="white"` turned into
 * a pale box with light text; dark mode gives the card the raised grey of
 * every other tooltip instead.
 */
export const tooltipCardBg = 'var(--kula-dark-secondary-200, #FFFFFF)';
